import { useEffect, useState } from "react";
import { Megaphone, Plus } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { api, errMsg } from "../lib/api";
import { Btn, Card, Field, Input, TextArea, PageHeader, Empty } from "../components/common";

function NewSpecial({ onDone }) {
  const { meta } = useAuth();
  const [f, setF] = useState({ name: "", purpose: "", beneficiary: "", collection_dates: "", participating_gkk_ids: [], receiving_fund: "", remittance_instructions: "", reporting_deadline: "", restrictions: "", active: true });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const save = async () => {
    try {
      await api.post("/special-collections", { ...f, collection_dates: f.collection_dates.split(",").map((s) => s.trim()).filter(Boolean) });
      toast.success("Announced. It is now a selectable collection category for the participating GKKs.");
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const toggle = (id) => setF({ ...f, participating_gkk_ids: f.participating_gkk_ids.includes(id) ? f.participating_gkk_ids.filter((x) => x !== id) : [...f.participating_gkk_ids, id] });
  return (
    <Card className="space-y-4 mb-6" data-testid="special-form">
      <h2 className="font-serif text-xl font-bold">Announce Special Collection</h2>
      <div className="grid sm:grid-cols-2 gap-4">
        <Field label="Official name"><Input data-testid="special-name-input" value={f.name} onChange={set("name")} /></Field>
        <Field label="Beneficiary"><Input data-testid="special-beneficiary-input" value={f.beneficiary} onChange={set("beneficiary")} /></Field>
        <Field label="Collection dates (comma separated, YYYY-MM-DD)"><Input data-testid="special-dates-input" value={f.collection_dates} onChange={set("collection_dates")} /></Field>
        <Field label="Reporting deadline"><Input type="date" data-testid="special-deadline-input" value={f.reporting_deadline} onChange={set("reporting_deadline")} /></Field>
        <Field label="Designated receiving fund / institution"><Input value={f.receiving_fund} onChange={set("receiving_fund")} /></Field>
        <Field label="Restrictions on use of proceeds"><Input value={f.restrictions} onChange={set("restrictions")} /></Field>
      </div>
      <Field label="Purpose"><TextArea data-testid="special-purpose-input" value={f.purpose} onChange={set("purpose")} /></Field>
      <Field label="Remittance instructions"><TextArea value={f.remittance_instructions} onChange={set("remittance_instructions")} /></Field>
      <div>
        <div className="font-semibold mb-2">Participating GKKs (none selected = all GKKs)</div>
        <div className="flex flex-wrap gap-2">{meta.gkks.map((g) => (
          <button key={g.id} type="button" onClick={() => toggle(g.id)} className={`h-11 px-4 rounded-full border-2 font-semibold ${f.participating_gkk_ids.includes(g.id) ? "bg-[#5C4033] text-white border-[#5C4033]" : "bg-white border-[#E4DACD]"}`}>{g.name}</button>))}
        </div>
      </div>
      <Btn v="terra" onClick={save} data-testid="special-save-button"><Megaphone className="h-5 w-5" /> Announce to GKKs</Btn>
    </Card>
  );
}

export default function Special() {
  const { can } = useAuth();
  const [items, setItems] = useState([]);
  const [show, setShow] = useState(false);
  const load = () => api.get("/special-collections").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  return (
    <div>
      <PageHeader title="Special Collections" subtitle="Designated collections are kept separate and must be remitted to their beneficiary."
        right={can("special:manage") && <Btn v="terra" data-testid="special-new-button" onClick={() => setShow(!show)}><Plus className="h-5 w-5" /> Announce</Btn>} />
      {show && <NewSpecial onDone={() => { setShow(false); load(); }} />}
      {items.length === 0 ? <Empty>No special collections announced yet.</Empty> : (
        <div className="grid md:grid-cols-2 gap-4 stagger">{items.map((s) => (
          <Card key={s.id} data-testid={`special-card-${s.id}`} className="border-l-4 border-l-[#C85A32]">
            <div className="flex justify-between gap-2"><h3 className="font-serif text-xl font-bold">{s.name}</h3><span className={`text-xs font-bold uppercase ${s.active ? "text-[#1B5E3F]" : "text-[#6E6259]"}`}>{s.active ? "Active" : "Closed"}</span></div>
            <p className="mt-2">{s.purpose}</p>
            <dl className="mt-3 text-sm grid grid-cols-[140px_1fr] gap-y-1">
              <dt className="text-[#6E6259]">Beneficiary</dt><dd>{s.beneficiary || "—"}</dd>
              <dt className="text-[#6E6259]">Dates</dt><dd>{s.collection_dates?.join(", ") || "—"}</dd>
              <dt className="text-[#6E6259]">Deadline</dt><dd>{s.reporting_deadline || "—"}</dd>
              <dt className="text-[#6E6259]">Receiving fund</dt><dd>{s.receiving_fund || "—"}</dd>
              <dt className="text-[#6E6259]">Remittance</dt><dd>{s.remittance_instructions || "—"}</dd>
              <dt className="text-[#6E6259]">Restrictions</dt><dd>{s.restrictions || "—"}</dd>
            </dl>
          </Card>))}
        </div>
      )}
    </div>
  );
}
